/**
 * LA reviews custom elements.
 * - <la-review-list> (sections/la-pdp-reviews.liquid): "Show more" reveals the next
 *   data-step hidden review cards and moves focus to the first one revealed; Read more
 *   on clamped review text.
 * - <la-reviews-carousel> (sections/la-reviews-carousel.liquid): arrows that loop,
 *   optional autoplay (data-autoplay ms, 0 = off) with a Pause button, and Read more
 *   on clamped review text.
 */
/**
 * Read more on review text clamped by CSS (.la-reviews__body). The button shows only when
 * the text is actually cut off; toggling adds .is-expanded to the review element.
 * Sets host.updateReadMore and host.resizeObserver.
 */
function initReadMore(host, itemSelector, onToggle) {
  host.updateReadMore = () => {
    host.querySelectorAll('[data-reviews-body]').forEach((body) => {
      const item = body.closest(itemSelector);
      const button = item?.querySelector('[data-reviews-more]');
      if (!button || item.classList.contains('is-expanded') || item.hidden) return;
      button.hidden = body.scrollHeight <= body.clientHeight + 1;
    });
  };
  host.updateReadMore();
  host.resizeObserver = new ResizeObserver(() => host.updateReadMore());
  host.resizeObserver.observe(host);

  host.addEventListener('click', (event) => {
    const button = event.target.closest('[data-reviews-more]');
    if (!button) return;
    const item = button.closest(itemSelector);
    const expanded = item.classList.toggle('is-expanded');
    button.setAttribute('aria-expanded', expanded);
    button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
    onToggle?.(expanded);
  });
}

if (!customElements.get('la-review-list')) {
  customElements.define(
    'la-review-list',
    class LaReviewList extends HTMLElement {
      connectedCallback() {
        initReadMore(this, '[data-review-card]');
        this.button = this.querySelector('[data-review-more]');
        if (!this.button) return;
        this.step = parseInt(this.dataset.step, 10) || 3;
        this.button.addEventListener('click', () => this.showMore());
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      showMore() {
        const hidden = Array.from(this.querySelectorAll('[data-review-card][hidden]'));
        const next = hidden.slice(0, this.step);
        next.forEach((card) => (card.hidden = false));
        // Cards that were hidden had no height to measure; check their clamp now.
        this.updateReadMore();
        if (next[0]) next[0].focus();
        if (hidden.length <= this.step) this.button.closest('.la-pdp-reviews__more').remove();
      }
    }
  );
}

if (!customElements.get('la-reviews-carousel')) {
  customElements.define(
    'la-reviews-carousel',
    class LaReviewsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-reviews-track]');
        this.slides = Array.from(this.querySelectorAll('[data-reviews-slide]'));
        if (!this.track || this.slides.length === 0) return;

        this.querySelector('[data-reviews-prev]')?.addEventListener('click', () => this.go(-1));
        this.querySelector('[data-reviews-next]')?.addEventListener('click', () => this.go(1));

        this.initReadMore();
        this.initAutoplay();
      }

      disconnectedCallback() {
        this.stop();
        this.resizeObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      /** Distance between two slides (slide width + gap). */
      step() {
        if (this.slides.length < 2) return this.track.clientWidth;
        return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
      }

      /** Scroll one slide; wraps to the other end so the carousel loops like REVIEWS.io. */
      go(direction) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const left = this.track.scrollLeft;
        if (direction > 0 && left >= max - 2) {
          this.track.scrollTo({ left: 0 });
        } else if (direction < 0 && left <= 2) {
          this.track.scrollTo({ left: max });
        } else {
          this.track.scrollBy({ left: direction * this.step() });
        }
      }

      initReadMore() {
        initReadMore(this, '.la-reviews__item', (expanded) => {
          // Someone reading a full review shouldn't have it scroll away.
          if (expanded) this.setUserPaused(true);
        });
      }

      initAutoplay() {
        this.delay = parseInt(this.dataset.autoplay, 10) || 0;
        this.pauseButton = this.querySelector('[data-reviews-pause]');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!this.delay || this.slides.length < 2 || reducedMotion) {
          // Drop only the button: the footer may also hold the Judge.me badge.
          const footer = this.pauseButton?.parentElement;
          this.pauseButton?.remove();
          this.pauseButton = null;
          if (footer && !footer.children.length) footer.remove();
          return;
        }

        this.userPaused = false;
        this.hovering = false;
        this.pauseButton?.addEventListener('click', () => this.setUserPaused(!this.userPaused));
        // A swipe means the shopper has taken over: stop until they press Play.
        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') this.setUserPaused(true);
        });
        this.addEventListener('mouseenter', () => { this.hovering = true; this.sync(); });
        this.addEventListener('mouseleave', () => { this.hovering = false; this.sync(); });
        this.addEventListener('focusin', () => { this.hovering = true; this.sync(); });
        this.addEventListener('focusout', (event) => {
          if (this.contains(event.relatedTarget)) return;
          this.hovering = false;
          this.sync();
        });
        this.onVisibility = () => this.sync();
        document.addEventListener('visibilitychange', this.onVisibility);
        this.sync();
      }

      setUserPaused(paused) {
        if (!this.pauseButton) return;
        this.userPaused = paused;
        const label = this.pauseButton.querySelector('[data-reviews-pause-label]');
        label.textContent = paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause;
        this.pauseButton.querySelector('.la-reviews__icon-pause').hidden = paused;
        this.pauseButton.querySelector('.la-reviews__icon-play').hidden = !paused;
        this.sync();
      }

      /** Run the timer only when nobody is interacting and the tab is visible. */
      sync() {
        const shouldRun = this.delay && !this.userPaused && !this.hovering && !document.hidden;
        if (shouldRun && !this.timer) {
          this.timer = setInterval(() => this.go(1), this.delay);
        } else if (!shouldRun) {
          this.stop();
        }
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}
